import {Link} from 'react-router-dom';

import {FileText, RotateCcw, Shield} from 'lucide-react';

import {GrAppsRounded} from 'react-icons/gr';
import {TbPhotoHeart} from 'react-icons/tb';

import {resetConsent} from '../Utils/CookieConsent';
import HtmlRenderer from '../Utils/HtmlRenderer';

import type {FooterNavItem, NavColumnProps} from '../declarations';

import LogoLight from '../assets/svg/font_light.svg';
import LogoDark from '../assets/svg/font_dark.svg';

/* -------------------- Constants -------------------- */
const LINK_CLS =
    'inline-flex items-center gap-2 text-sm text-[rgb(var(--text-secondary))] ' +
    'hover:text-[rgb(var(--text-primary))] hover:translate-x-0.5 transition-all ' +
    'rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))]';

const NAV_COLUMNS: readonly NavColumnProps[] = [
    {
        heading: 'Products',
        items: [
            {to: '/digital', icon: GrAppsRounded, label: 'Digital'},
            {to: '/print', icon: TbPhotoHeart, label: 'Print'},
        ],
    },
    {
        heading: 'Legal',
        showCookieReset: true,
        items: [
            {to: '/imprint', icon: FileText, label: 'Imprint'},
            {to: '/privacy', icon: Shield, label: 'Privacy'},
        ],
    },
];

/* -------------------- Helpers -------------------- */
const handleResetCookies = (): void => {
    resetConsent();
    window.location.reload();
};

/* -------------------- Navigation Item -------------------- */
const NavLinkItem = ({to, icon: Icon, label}: FooterNavItem) => (
    <li>
        <Link to={to} className={LINK_CLS}>
            <Icon size={14} aria-hidden='true' />
            {label}
        </Link>
    </li>
);

/* -------------------- Navigation Column -------------------- */
const NavColumn = ({
    heading,
    items,
    showCookieReset = false,
}: NavColumnProps) => (
    <div>
        <h3 className='text-xs font-semibold uppercase tracking-wider mb-4 text-[rgb(var(--text-secondary))]'>
            {heading}
        </h3>
        <ul className='space-y-3'>
            {items.map(({to, icon, label}) => (
                <NavLinkItem key={to} to={to} icon={icon} label={label} />
            ))}
            {showCookieReset && (
                <li>
                    <button
                        type='button'
                        onClick={handleResetCookies}
                        aria-label='Reset cookie preferences'
                        className={`${LINK_CLS} w-full text-left`}
                    >
                        <RotateCcw size={14} aria-hidden='true' />
                        Reset Cookies
                    </button>
                </li>
            )}
        </ul>
    </div>
);

/* -------------------- Footer -------------------- */
const Footer = () => (
    <footer className='theme-transition bg-[rgb(var(--bg-secondary))] border-t border-[rgb(var(--border))] mt-12'>
        <div className='page-container py-12 sm:py-14'>
            <div className='grid grid-cols-1 gap-10 sm:grid-cols-3'>
                {/* -------------------- Brand Section -------------------- */}
                <div className='sm:col-span-1'>
                    <img
                        src={LogoDark}
                        alt='Logo'
                        className='h-6 w-auto dark:hidden'
                    />
                    <img
                        src={LogoLight}
                        alt='Logo'
                        className='h-6 w-auto hidden dark:block'
                    />

                    <HtmlRenderer
                        html='Professional Zendesk apps and beautifully crafted print products.'
                        tag='p'
                        className='mt-3 max-w-xs text-sm leading-relaxed text-[rgb(var(--text-secondary))]'
                    />
                </div>

                {/* -------------------- Navigation Columns -------------------- */}
                {NAV_COLUMNS.map((column) => (
                    <NavColumn key={column.heading} {...column} />
                ))}
            </div>

            {/* -------------------- Footer Bottom Bar -------------------- */}
            <div className='mt-10 flex flex-col items-center justify-between gap-2 border-t border-[rgb(var(--border))] pt-6 text-xs text-[rgb(var(--text-secondary))] sm:flex-row'>
                <p>© {new Date().getFullYear()} es2be. All rights reserved.</p>
                <p className='opacity-60'>es2be.de</p>
            </div>
        </div>
    </footer>
);

export default Footer;
